// Admin — Create Shift modal.
// Spec: docs/admin-shift-management.md §2.1 BR-01..BR-09a, §3.1, §7.2

import { useEffect, useState } from 'react';
import { Form, Input, Modal, Select, TimePicker, notification } from 'antd';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { TYPE_DEPARTMENT } from 'constants/common';
import { AdminCreateShiftDto, CreateShiftResponse } from 'types/shift';
import { mapShiftError } from '../helpers/shift-error';
import { DEPARTMENT_LABEL } from 'pages/star-shared/labels';
import { sameTime } from '../helpers/shift-time';

interface Props {
  open: boolean;
  /** Bấm "Thêm ca" trong thẻ một phòng ban → chọn sẵn phòng ban đó. */
  defaultDepartment?: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

const departmentOptions = Object.values(TYPE_DEPARTMENT).map((v) => ({
  value: v,
  label: DEPARTMENT_LABEL[v] || v,
}));

const CreateShiftModal = ({ open, defaultDepartment, onClose, onSuccess }: Props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && defaultDepartment) form.setFieldsValue({ department: defaultDepartment });
  }, [open, defaultDepartment, form]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: any) => {
    const startTime = values.startTime?.format('HH:mm');
    const endTime = values.endTime?.format('HH:mm');

    // BR-05 client-side guard.
    if (sameTime(startTime, endTime)) {
      notification.warning({
        message: 'Giờ bắt đầu và kết thúc không được trùng nhau.',
      });
      return;
    }

    const dto: AdminCreateShiftDto = {
      name: (values.name || '').trim(),
      department: values.department,
      startTime,
      endTime,
    };

    setLoading(true);
    try {
      const res: any = await AxiosUtils.post<AdminCreateShiftDto, CreateShiftResponse>(
        API.attendanceShifts,
        dto
      );
      const body: CreateShiftResponse = res?.data ?? res;
      notification.success({
        message: 'Đã tạo ca làm.',
        description:
          typeof body?.preferencesCreated === 'number'
            ? `Đã thêm ca này vào lựa chọn của ${body.preferencesCreated}/${body.usersTargeted} nhân viên cùng phòng ban.`
            : undefined,
      });
      onSuccess();
      handleClose();
    } catch (err: any) {
      // BR-01: 409 SHIFT_NAME_EXISTS → inline error on `name`.
      const raw = Array.isArray(err?.message) ? err.message[0] : err?.message;
      if (err?.statusCode === 409 || raw?.includes?.('SHIFT_NAME_EXISTS')) {
        form.setFields([{ name: 'name', errors: ['Tên ca đã tồn tại.'] }]);
        return;
      }
      notification.error({ message: mapShiftError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Tạo ca làm"
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      maskClosable={false}
      width={520}
    >
      <Form form={form} layout="vertical" onFinish={onFinish} className="custom-form">
        <Form.Item
          label="Tên ca"
          name="name"
          rules={[
            { required: true, message: 'Vui lòng nhập tên ca.' },
            { whitespace: true, message: 'Tên ca không được để trống.' },
            { max: 100, message: 'Tên ca tối đa 100 ký tự.' },
          ]}
        >
          <Input placeholder="VD: HR 09:00-18:00" maxLength={100} />
        </Form.Item>
        <Form.Item
          label="Phòng ban"
          name="department"
          rules={[{ required: true, message: 'Vui lòng chọn phòng ban.' }]}
        >
          <Select placeholder="Chọn phòng ban" options={departmentOptions} />
        </Form.Item>
        <Form.Item
          label="Giờ bắt đầu"
          name="startTime"
          rules={[{ required: true, message: 'Vui lòng chọn giờ bắt đầu.' }]}
        >
          <TimePicker format="HH:mm" style={{ width: '100%' }} minuteStep={5} />
        </Form.Item>
        <Form.Item
          label="Giờ kết thúc"
          name="endTime"
          rules={[{ required: true, message: 'Vui lòng chọn giờ kết thúc.' }]}
          extra="Cho phép ca xuyên đêm (kết thúc nhỏ hơn bắt đầu)."
        >
          <TimePicker format="HH:mm" style={{ width: '100%' }} minuteStep={5} />
        </Form.Item>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton text="Tạo" bgColor="secondary-green" htmlType="submit" loading={loading} />
        </div>
      </Form>
    </Modal>
  );
};

export default CreateShiftModal;
